import { Icon } from "@/components/atoms/Icon";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Typography } from "@/components/ui/typography";
import {
  addDays,
  addHours,
  addMilliseconds,
  addMinutes,
  addMonths,
  addSeconds,
  addYears,
  format,
  subDays,
  subHours,
  subMilliseconds,
  subMinutes,
  subMonths,
  subSeconds,
  subYears,
} from "date-fns";
import {
  motion,
  MotionValue,
  useMotionValueEvent,
  useTransform,
} from "framer-motion";
import { memo, useCallback, useEffect, useRef } from "react";
import {
  PauseIcon,
  PlayIcon,
  SkipBackIcon,
  SkipForwardIcon,
  StepBackIcon,
  StepForwardIcon,
} from "lucide-react";
import { formatDelta } from "./Brush.utils";
import { BrushControlDurationSlider } from "./BrushControlDurationSlider";
import { BrushControlPlaybackSpeedSlider } from "./BrushControlPlaybackSpeedSlider";

// Helper functions for date manipulation
const getNext = (
  date: Date,
  unit:
    | "year"
    | "month"
    | "day-weekday-name"
    | "day-month-number"
    | "hour"
    | "minute"
    | "second"
    | "millisecond"
    | "am-pm",
): Date => {
  switch (unit) {
    case "year":
      return addYears(date, 1);
    case "month":
      return addMonths(date, 1);
    case "day-weekday-name":
    case "day-month-number":
      return addDays(date, 1);
    case "hour":
      return addHours(date, 1);
    case "minute":
      return addMinutes(date, 1);
    case "second":
      return addSeconds(date, 1);
    case "millisecond":
      return addMilliseconds(date, 1);
    case "am-pm":
      // Toggle AM/PM by adding 12 hours
      return addHours(date, 12);
    default:
      return date;
  }
};

const getPrev = (
  date: Date,
  unit:
    | "year"
    | "month"
    | "day-weekday-name"
    | "day-month-number"
    | "hour"
    | "minute"
    | "second"
    | "millisecond"
    | "am-pm",
): Date => {
  switch (unit) {
    case "year":
      return subYears(date, 1);
    case "month":
      return subMonths(date, 1);
    case "day-weekday-name":
    case "day-month-number":
      return subDays(date, 1);
    case "hour":
      return subHours(date, 1);
    case "minute":
      return subMinutes(date, 1);
    case "second":
      return subSeconds(date, 1);
    case "millisecond":
      return subMilliseconds(date, 1);
    case "am-pm":
      // Toggle AM/PM by subtracting 12 hours
      return subHours(date, 12);
    default:
      return date;
  }
};

function ScrollableTimeUnit({
  timestampMotionValue,
  label,
  unit,
  separator = "",
  onTimeChange,
  children,
  startSpring,
}: {
  timestampMotionValue: MotionValue<number>;
  label: string;
  unit:
    | "year"
    | "month"
    | "day-weekday-name"
    | "day-month-number"
    | "hour"
    | "minute"
    | "second"
    | "millisecond"
    | "am-pm";
  separator: string;
  onTimeChange: (newTime: number) => void;
  startSpring: () => void;
  children: (ref: React.RefObject<HTMLElement>) => React.ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);

  const formatValue = useCallback(
    (timestamp: number) => {
      const date = new Date(timestamp);
      switch (unit) {
        case "year":
          return format(date, "yyyy");
        case "month":
          // Jan, Feb, etc.
          return format(date, "MMM");
        case "day-weekday-name":
          // Format as "Tuesday, 3rd", etc.
          return (() => {
            const dayOfWeek = format(date, "EEEE");
            const dayOfMonth = date.getDate();
            // Helper to get ordinal suffix
            function getOrdinal(n: number) {
              const s = ["th", "st", "nd", "rd"];
              const v = n % 100;
              return s[(v - 20) % 10] || s[v] || s[0];
            }
            return `${dayOfWeek.slice(0, 3)}`;
          })();
        case "day-month-number":
          return format(date, "dd");
        case "hour":
          // 12 hour clock
          return format(date, "h");
        case "minute":
          return format(date, "mm");
        case "second":
          // 24 hour clock, no AM/PM
          return format(date, "ss");
        case "millisecond":
          // 24 hour clock, no AM/PM
          return format(date, "SSS");
        case "am-pm":
          return format(date, "a");
        default:
          return "";
      }
    },
    [unit],
  );

  const updateContent = useCallback(
    (timestamp: number) => {
      if (ref.current) {
        const displayValue = formatValue(timestamp) + separator;
        // Update the content directly using DOM manipulation
        ref.current.textContent = displayValue;
      }
    },
    [formatValue],
  );

  useEffect(() => {
    const unsubscribe = timestampMotionValue.on("change", (latest) => {
      updateContent(latest);
    });
    // Set initial value
    updateContent(timestampMotionValue.get());
    return unsubscribe;
  }, [timestampMotionValue, updateContent]);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const currentTime = timestampMotionValue.get();
      const currentDate = new Date(currentTime);

      // Use getNext for scroll up (negative deltaY) and getPrev for scroll down (positive deltaY)
      const newDate =
        e.deltaY > 0 ? getPrev(currentDate, unit) : getNext(currentDate, unit);
      const newTime = newDate.getTime();
      startSpring();

      onTimeChange(newTime);
    },
    [timestampMotionValue, unit, onTimeChange],
  );

  return (
    <motion.div
      style={{
        cursor: "ns-resize",
        userSelect: "none",
        display: "inline-block",
      }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      onWheel={handleWheel}
    >
      {children(ref)}
    </motion.div>
  );
}

function DeltaDisplay({
  startTimeMotionValue,
  endTimeMotionValue,
}: {
  startTimeMotionValue: MotionValue<number>;
  endTimeMotionValue: MotionValue<number>;
}) {
  const deltaRef = useRef<HTMLSpanElement>(null);

  // Use useTransform to always keep the delta up to date
  const deltaMotionValue = useTransform(() => {
    const deltaSeconds =
      (endTimeMotionValue.get() - startTimeMotionValue.get()) / 1000;
    return formatDelta(deltaSeconds);
  });

  useEffect(() => {
    const unsubscribe = deltaMotionValue.on("change", (latest) => {
      if (deltaRef.current) {
        deltaRef.current.textContent = latest;
      }
    });
    // Set initial value
    if (deltaRef.current) {
      deltaRef.current.textContent = deltaMotionValue.get();
    }
    return unsubscribe;
  }, [deltaMotionValue]);

  return (
    <Typography
      ref={deltaRef}
      level="mono-body-md"
      className="text-muted-foreground"
    />
  );
}

const BrushControls = memo(function BrushClock({
  cursorPlaybackSpeedMotionValue,
  cursorTimeMotionValue: clockMotionValue,
  cursorDurationMotionValue,
  startTimeMotionValue,
  endTimeMotionValue,
  onTogglePause,
  onToggleMode,
  onTimeChange,
  onDurationChange,
  onPlaybackSpeedChange,
  onNextEvent,
  onPreviousEvent,
  startSpring,
  isPaused,
  isLive,
}: {
  cursorPlaybackSpeedMotionValue: MotionValue<number>;
  cursorTimeMotionValue: MotionValue<number>;
  cursorDurationMotionValue: MotionValue<number>;
  startTimeMotionValue: MotionValue<number>;
  endTimeMotionValue: MotionValue<number>;
  onTogglePause: () => void;
  onToggleMode: () => void;
  onTimeChange: (newTime: number) => void;
  onDurationChange: (duration: number) => void;
  onPlaybackSpeedChange: (speed: number) => void;
  onNextEvent: () => void;
  onPreviousEvent: () => void;
  startSpring: () => void;
  isPaused: boolean;
  isLive: boolean;
}) {
  return (
    <div className="flex w-full justify-between flex-row items-center [border-top-width:1px] [border-top-style:solid] border-border pl-4 pr-4 gap-2">
      <DeltaDisplay
        startTimeMotionValue={clockMotionValue}
        endTimeMotionValue={startTimeMotionValue}
      />
      <div className="flex flex-row items-center">
        {/* Mode Toggle Button */}
        <div
          // border: 1,
          // borderColor: "divider",
          className="flex flex-row items-center pl-4 pr-4 gap-4 pt-4 pb-4 rounded-sm bg-muted"
        >
          {/* (Timestamp) */}
          <div className="flex flex-col items-center">
            <div className="flex flex-row items-center">
              <ScrollableTimeUnit
                timestampMotionValue={clockMotionValue}
                label="Hour"
                unit="hour"
                separator=":"
                onTimeChange={onTimeChange}
                startSpring={startSpring}
              >
                {(ref) => (
                  <Typography level="mono-h2" ref={ref}>
                    :
                  </Typography>
                )}
              </ScrollableTimeUnit>
              <ScrollableTimeUnit
                timestampMotionValue={clockMotionValue}
                label="Min"
                unit="minute"
                separator=":"
                onTimeChange={onTimeChange}
                startSpring={startSpring}
              >
                {(ref) => (
                  <Typography level="mono-h2" ref={ref}>
                    :
                  </Typography>
                )}
              </ScrollableTimeUnit>
              <ScrollableTimeUnit
                timestampMotionValue={clockMotionValue}
                label="Sec"
                unit="second"
                separator="."
                onTimeChange={onTimeChange}
                startSpring={startSpring}
              >
                {(ref) => (
                  <Typography level="mono-h2" ref={ref}>
                    .
                  </Typography>
                )}
              </ScrollableTimeUnit>
              <div
                // border: 1,
                // pt: 0.6,
                className="flex flex-col items-start pb-[0.8px] gap-[1.6px]"
              >
                <ScrollableTimeUnit
                  timestampMotionValue={clockMotionValue}
                  label="AM/PM"
                  unit="am-pm"
                  separator=""
                  onTimeChange={onTimeChange}
                  startSpring={startSpring}
                >
                  {(ref) => (
                    <Typography
                      level="mono-body-sm"
                      ref={ref}
                      className="[line-height:0.8] text-[10px] text-muted-foreground/80"
                    />
                  )}
                </ScrollableTimeUnit>
                <ScrollableTimeUnit
                  timestampMotionValue={clockMotionValue}
                  label="Ms"
                  unit="millisecond"
                  separator=""
                  onTimeChange={onTimeChange}
                  startSpring={startSpring}
                >
                  {(ref) => (
                    <Typography
                      level="mono-body-md"
                      ref={ref}
                      // fontSize: "0.8",
                      className="[line-height:0.8] text-muted-foreground"
                    />
                  )}
                </ScrollableTimeUnit>
              </div>
            </div>
            <div className="flex flex-row items-baseline gap-2">
              <ScrollableTimeUnit
                timestampMotionValue={clockMotionValue}
                label="Day"
                unit="day-weekday-name"
                separator=", "
                onTimeChange={onTimeChange}
                startSpring={startSpring}
              >
                {(ref) => (
                  <Typography level="mono-body-lg" ref={ref}>
                    ,
                  </Typography>
                )}
              </ScrollableTimeUnit>
              <ScrollableTimeUnit
                timestampMotionValue={clockMotionValue}
                label="Month"
                unit="month"
                separator=""
                onTimeChange={onTimeChange}
                startSpring={startSpring}
              >
                {(ref) => <Typography level="mono-body-lg" ref={ref} />}
              </ScrollableTimeUnit>
              <ScrollableTimeUnit
                timestampMotionValue={clockMotionValue}
                label="Day"
                unit="day-month-number"
                separator=", "
                onTimeChange={onTimeChange}
                startSpring={startSpring}
              >
                {(ref) => (
                  <Typography level="mono-body-lg" ref={ref}>
                    ,
                  </Typography>
                )}
              </ScrollableTimeUnit>
              <ScrollableTimeUnit
                timestampMotionValue={clockMotionValue}
                label="Year"
                unit="year"
                separator=""
                onTimeChange={onTimeChange}
                startSpring={startSpring}
              >
                {(ref) => <Typography level="mono-body-lg" ref={ref} />}
              </ScrollableTimeUnit>
            </div>
          </div>

          <div className="w-px self-stretch bg-border" />

          {/* (Pause/Play and Mode) */}
          <div>
            <ButtonGroup>
              {/* Full Rewind */}
              <Button variant="outline" disabled={!isPaused}>
                <Icon ReactIcon={SkipBackIcon} size="lg" />
              </Button>
              {/* Rewind */}
              <Button
                variant="outline"
                disabled={!isPaused}
                onClick={onPreviousEvent}
              >
                <Icon ReactIcon={StepBackIcon} size="lg" />
              </Button>
              {/* Play / Pause */}
              <Button variant="outline" onClick={onTogglePause}>
                <Icon ReactIcon={isPaused ? PlayIcon : PauseIcon} size="xl" />
              </Button>
              {/* Forward */}
              <Button
                variant="outline"
                disabled={!isPaused}
                onClick={onNextEvent}
              >
                <Icon ReactIcon={StepForwardIcon} size="lg" />
              </Button>
              {/* Full Forward */}
              <Button
                variant="outline"
                onClick={onToggleMode}
                disabled={isLive}
              >
                <Icon ReactIcon={SkipForwardIcon} size="lg" />
              </Button>
            </ButtonGroup>
          </div>

          <div className="w-px self-stretch bg-border" />
          <BrushControlDurationSlider
            cursorDurationMotionValue={cursorDurationMotionValue}
            onDurationChange={onDurationChange}
          />
          <div className="w-px self-stretch bg-border" />
          <BrushControlPlaybackSpeedSlider
            cursorPlaybackSpeedMotionValue={cursorPlaybackSpeedMotionValue}
            onPlaybackSpeedChange={onPlaybackSpeedChange}
            isLive={isLive}
          />
        </div>
      </div>

      <DeltaDisplay
        startTimeMotionValue={clockMotionValue}
        endTimeMotionValue={endTimeMotionValue}
      />
    </div>
  );
});
export { BrushControls };
